import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  statsDateRangeAtom,
  type StatsDateRange,
} from "@/stores/atoms/stats-date-range-atom";
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { useAtom } from "jotai";
import { CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import type { Locale } from "date-fns";

const DATE_FNS_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
};

const PRESETS = [
  {
    labelKey: "keys.date_range_this_week",
    getRange: () => {
      const now = new Date();
      return {
        from: startOfWeek(now, { weekStartsOn: 1 }),
        to: endOfWeek(now, { weekStartsOn: 1 }),
      };
    },
  },
  {
    labelKey: "keys.date_range_this_month",
    getRange: () => {
      const now = new Date();
      return { from: startOfMonth(now), to: endOfMonth(now) };
    },
  },
  {
    labelKey: "keys.date_range_this_year",
    getRange: () => {
      const now = new Date();
      return { from: startOfYear(now), to: endOfYear(now) };
    },
  },
] as const;

function toAtomValue(range: DateRange): StatsDateRange | null {
  if (!range.from || !range.to) return null;
  return {
    start_date: format(range.from, "yyyy-MM-dd"),
    end_date: format(range.to, "yyyy-MM-dd"),
  };
}

function atomToDateRange(range: StatsDateRange): DateRange {
  return {
    from: new Date(range.start_date),
    to: new Date(range.end_date),
  };
}

const StatsDateRangeSelector = () => {
  const { t, i18n } = useTranslation();
  const [atomRange, setAtomRange] = useAtom(statsDateRangeAtom);
  const [open, setOpen] = useState(false);
  const [localRange, setLocalRange] = useState<DateRange>(
    atomToDateRange(atomRange),
  );

  const locale = useMemo(
    () => DATE_FNS_LOCALES[i18n.resolvedLanguage ?? "fr"] ?? fr,
    [i18n.resolvedLanguage],
  );

  const handleSelect = (range: DateRange | undefined) => {
    if (!range) return;
    setLocalRange(range);
    const value = toAtomValue(range);
    if (value) {
      setAtomRange(value);
      setOpen(false);
    }
  };

  const handlePreset = (getRange: () => { from: Date; to: Date }) => {
    const range = getRange();
    setLocalRange(range);
    setAtomRange({
      start_date: format(range.from, "yyyy-MM-dd"),
      end_date: format(range.to, "yyyy-MM-dd"),
    });
    setOpen(false);
  };

  const label =
    atomRange.start_date && atomRange.end_date
      ? `${format(new Date(atomRange.start_date), "dd/MM/yyyy")} – ${format(new Date(atomRange.end_date), "dd/MM/yyyy")}`
      : t("keys.date_range_placeholder");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="justify-start text-gray-600 hover:text-gray-600"
        >
          <CalendarIcon className="size-4 text-gray-600" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex flex-wrap gap-2 p-3 border-b bg-gray-50">
          {PRESETS.map((preset) => (
            <Button
              key={preset.labelKey}
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => handlePreset(preset.getRange)}
            >
              {t(preset.labelKey)}
            </Button>
          ))}
        </div>
        <Calendar
          mode="range"
          selected={localRange}
          onSelect={handleSelect}
          numberOfMonths={2}
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
};

export default StatsDateRangeSelector;
